import { Controller } from 'stimulus'
import { AmortizationGraph } from 'classes/amortization-graph'
import { ScrollyAmortizationTable } from 'classes/scrolly-amortization-table'

import * as MortMath from 'modules/mort-math'
import * as MortDataPrep from 'modules/mort-data-prep'
import moment from 'moment'

import * as MomentHelp from 'helpers/moment-helper'
import parseFloatString from 'helpers/parse-float-string'
import formatCurrency from 'helpers/new-format-currency'

/**
 * Manages js for the amortization calculator page (StimulusJS controller)
 *
 * @class AmortizationCalculatorController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'loanAmount',
    'loanTerm',
    'interestRatePercent',
    'loanStartDate',
    'additionalMonthlyPayment',
    'resultPanelMonthlyPayment',
    'resultPanelPayoffDate',
    'resultPanelOverallTotal',
    'resultPanelNumberOfPayments',
    'resultPanelTotalInterestPaid',
    'infoSentenceLoanYearDuration',
    'payoffDateInformation',
    'overallTerm',
    'totalInterest',
    'amortizationGraph',
    'amortizationTable',
    'toggleAmortizationTable'
  ]

  timeout = null

  /**
   * @function Controllers.AmortizationCalculatorController.initialize
   * @memberof Controllers
   * @description Called on page load. Calculates both the current and new mortgage payments differences
   *
   * @return {undefined} manipulates DOM
   */
  initialize() {
    this.termToggle = 'monthly'

    const { schedule, monthlyPaymentWithAdditional } = this._calculateData()

    if (this.hasAmortizationGraphTarget) {
      const { graphData } = this._prepareGraphData(schedule)

      this.amortizationGraph = new AmortizationGraph(this.amortizationGraphTarget, 'Yrs')
      this.amortizationGraph.build(graphData)
    }

    if (this.hasAmortizationTableTarget) {
      const amortizationTableData = MortDataPrep.monthlyTableData(schedule, monthlyPaymentWithAdditional, 'YYYY/M')
      this.amortizationTable      = new ScrollyAmortizationTable(this.amortizationTableTarget)
      this.amortizationTable.build(amortizationTableData)
    }
  }

  /**
   * @function Controllers.AmortizationCalculatorController.process
   * @memberof Controllers
   * @description Called on `keyup` event and `toggle()`. Calculates both the current and new mortgage payments differences
   *
   * @return {undefined} manipulates DOM
   */
  process() {
    clearTimeout(this.timeout)

    if (this.hasAmortizationTableTarget) {
      this.amortizationTableTarget.dispatchEvent(new CustomEvent('table:collapse'))
    }

    this.timeout = setTimeout(() => {
      // Validates date fields on "keyup" - Don't calculate unless date field is valid
      if (!this._isStringDateValid(this.loanStartDateTarget.value)) {
        return false
      }

      const { schedule, textualData, monthlyPaymentWithAdditional } = this._calculateData()

      const { graphData, yearlySchedule } = this._prepareGraphData(schedule)

      if (this.amortizationGraph) {
        this.amortizationGraph.build(graphData)
      }

      let amortizationTableData

      if (this.termToggle === 'annually') {
        amortizationTableData = MortDataPrep.yearlyTableData(yearlySchedule, monthlyPaymentWithAdditional, 'YYYY')
      } else {
        amortizationTableData = MortDataPrep.monthlyTableData(schedule, monthlyPaymentWithAdditional, 'YYYY/M')
      }

      if (this.amortizationTable) {
        this.amortizationTable.build(amortizationTableData)
      }

      this._setTextualData(textualData, monthlyPaymentWithAdditional)
    }, 300)
  }


  /**
   * @function Controllers.AmortizationCalculatorController.toggle
   * @memberof Controllers
   * @description Toggles graph between monthly and yearly. Called on `click` event
   *
   * @param {Event} event - Event from user interaction
   *
   * @return {undefined} manipulates DOM
   */
  toggle(event) {
    const toggleButtons = document.querySelectorAll(`[data-target="${this.toggleAmortizationTableTarget.dataset.target}"]`)

    Array.from(toggleButtons).map(button => {
      button.classList.remove('mortgage-calculator__amortization-information-toggle-button--active')
    })

    event.target.classList.add('mortgage-calculator__amortization-information-toggle-button--active')

    // Sets toggle to "monthly" or "annually".
    this.termToggle = event.target.dataset.value

    this.process()
  }


  // private


  /**
   * @function Controllers.AmortizationCalculatorController._setTextualData
   * @memberof Controllers
   * @description Displays the textual calculated results
   * @private
   *
   * @param {Object} textualData
   * @param {Float} textData.totalInterest
   * @param {Float} textData.totalOverall
   * @param {String} textData.loanEnd
   * @param {Integer} textData.loanDurationInYears
   * @param {Integer} textData.numberOfPayments
   *
   * @param {Float} monthlyPaymentWithAdditional
   *
   * @return {undefined} manipulates DOM
   */
  _setTextualData(textualData, monthlyPaymentWithAdditional) {
    this._setResultPanel(textualData, monthlyPaymentWithAdditional)
    this._setInfoSentence(textualData)
  }

  /**
   * @function Controllers.AmortizationCalculatorController._setResultPanel
   * @memberof Controllers
   * @description Displays the textual calculated results for the result panel section
   * @private
   *
   * @param {Object} textualData
   * @param {Float} textData.totalInterest
   * @param {Float} textData.totalOverall
   * @param {String} textData.loanEnd
   * @param {Integer} textData.loanDurationInYears
   * @param {Integer} textData.numberOfPayments
   *
   * @param {Float} monthlyPaymentWithAdditional
   *
   * @return {undefined} manipulates DOM
   */
  _setResultPanel(textualData, monthlyPaymentWithAdditional) {
    this.resultPanelMonthlyPaymentTarget.textContent    = formatCurrency(monthlyPaymentWithAdditional)
    this.resultPanelPayoffDateTarget.textContent        = textualData.loanEnd
    this.resultPanelOverallTotalTarget.textContent      = formatCurrency(textualData.totalOverall)
    this.resultPanelNumberOfPaymentsTarget.textContent  = textualData.numberOfPayments
    this.resultPanelTotalInterestPaidTarget.textContent = formatCurrency(textualData.totalInterest)
  }

  /**
   * @function Controllers.AmortizationCalculatorController._setInfoSentence
   * @memberof Controllers
   * @description Displays the textual calculated results for the result panel section
   * @private
   *
   * @param {Object} textualData
   * @param {Float} textData.totalInterest
   * @param {Float} textData.totalOverall
   * @param {String} textData.loanEnd
   * @param {Integer} textData.loanDurationInYears
   * @param {Integer} textData.numberOfPayments
   *
   * @return {undefined} manipulates DOM
   */
  _setInfoSentence(textualData) {
    if (this.hasInfoSentenceLoanYearDurationTarget) {
      this.infoSentenceLoanYearDurationTarget.textContent = textualData.loanDurationInYears
    }
    this.payoffDateInformationTarget.textContent        = textualData.loanEnd
    this.totalInterestTarget.textContent                = formatCurrency(textualData.totalInterest)
  }

  /**
   * @function Controllers.AmortizationCalculatorController._calculateData
   * @memberof Controllers
   * @description A phoenix rises from the ashes of calculations past... Takes inputs from the UI and gets calculations from MortMath
   * @private
   *
   * @return {Object} textualData (object), schedule, monthlyPaymentWithAdditional
   *
   * @example
   *
   * return = {
   *   textualData: {
   *     loanEnd:             Integer,
   *     totalInterest:       Float,
   *     totalOverall:        Float,
   *     numberOfPayments:    Integer,
   *     loanDurationInYears: Integer,
   *   },
   *   schedule,
   *   monthlyPaymentWithAdditional,
   * }
   *
   */
  _calculateData() {
    const data =  this._prepareFormValues()

    const monthlyInterestPercent = MortMath.monthlyInterestPercent(data.interestRatePercent)
    const monthlyPayment         = MortMath.monthlyPayment({
      loanTermInMonths:       data.loanTermInMonths,
      monthlyInterestPercent: monthlyInterestPercent,
      principal:              data.loanAmount
    })

    const loanDetails = {
      balance:                 data.loanAmount || 0,
      date:                    data.loanStartDate,
      monthlyInterestPercent:  monthlyInterestPercent,
      monthlyPayment:          monthlyPayment,
      additionalPaymentAmount: data.additionalPayment,
      additionalPaymentStart:  moment()
    }

    const schedule            = MortMath.amortizationSchedule(loanDetails)
    const loanEnd             = MortDataPrep.loanEnd(schedule)
    const totalInterest       = MortMath.totalInterestFrom(schedule, loanDetails.date)
    const totalOverall        = totalInterest + data.loanAmount
    const numberOfPayments    = Object.keys(schedule).length
    const loanDurationInYears = MortDataPrep.loanDurationInYears(schedule)

    const monthlyPaymentWithAdditional = monthlyPayment + data.additionalPayment

    const textualData = {
      loanEnd,
      totalInterest,
      totalOverall,
      numberOfPayments,
      loanDurationInYears
    }

    return {
      textualData,
      schedule,
      monthlyPaymentWithAdditional
    }
  }

  /**
   * @function Controllers.AmortizationCalculatorController._prepareFormValues
   * @memberof Controllers
   * @description Takes inputs from the original loan form and prepares them for MortMath consumption
   * @private
   *
   * @return {Object} - loanAmount, loanTerm, additionalPayment, loanStartDate, loanTermInMonths, interestRatePercent
   *
   * @example
   *
   * return = {
   *   loanAmount:          300000,
   *   loanTerm:            30,
   *   additionalPayment:   0,
   *   loanStartDate:       moment,
   *   loanTermInMonths:    360,
   *   interestRatePercent: 5.125,
   * }
   */
  _prepareFormValues() {
    const loanAmount          = parseFloatString(this.loanAmountTarget.value)
    const loanTerm            = parseFloatString(this.loanTermTarget.value)
    const additionalPayment   = parseFloatString(this.additionalMonthlyPaymentTarget.value)
    const interestRatePercent = parseFloatString(this.interestRatePercentTarget.value)
    const parsedLoanStartDate = MomentHelp.formatForMomentMonthFirst(this.loanStartDateTarget.value)

    const loanStartDate    = moment(parsedLoanStartDate)
    const loanTermInMonths = loanTerm * 12

    return {
      loanAmount,
      loanTerm,
      additionalPayment,
      loanStartDate,
      loanTermInMonths,
      interestRatePercent
    }
  }

  /**
   * @function Controllers.AmortizationCalculatorController._prepareGraphData
   * @memberof Controllers
   * @description Returns formatted data from a schedule suitable for d3 graph
   * @private
   *
   * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   *
   * @return {Object} data
   * @return {Array.<Object>} data.graphData - labels and data for a loan
   * @return {Object} data.yearlySchedule - the yearly schedule
   *
   * @example
   *
   * returns = {
   *   graphData: [
   *     {
   *       label: "Yrs",
   *       data: [ 0, 1, 2, ... ],
   *     },
   *     {
   *       label: "Interest",
   *       data: [ 5098.822433980482, 17185.782102743888, 29053.507463941045, ... ],
   *     },
   *     {
   *       label: "Principal",
   *       data: [ 2110.342336927378, 7325.378118342836, 12759.648207324546, ... ]
   *      },
   *     {
   *       label: "Remaining",
   *       data: [ 295389.6576630726, 290174.62188165705, 284740.3517926753, ... ]
   *     },
   *   ],
   *   yearlySchedule: schedule, - reslut of [MortMath.yearlyAmortizationSchedule]{@link module:MortMath.yearlyAmortizationSchedule}
   */
  _prepareGraphData(schedule) {
    const yearlySchedule = MortMath.yearlyAmortizationSchedule(schedule)
    const yearlyData     = MortMath.yearlyOverallTrendingValues(yearlySchedule)

    const graphData = MortDataPrep.graphData(yearlyData)

    return { graphData, yearlySchedule }
  }


  /**
   * @function Controllers.AmortizationCalculatorController._isStringDateValid
   * @memberof Controllers
   * @description Determines if the number of digits might be a valid date
   * @private
   *
   * @param {String} stringMonthYearDate
   *
   * @return {Boolean}
   */
  _isStringDateValid(stringMonthYearDate) {
    const splitDate = stringMonthYearDate.split('/')

    return splitDate.length === 2 && splitDate[0].length === 2 && splitDate[1].length === 4
  }
}
